import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Upload, Loader2, CheckCircle, AlertTriangle, Brain, FolderOpen,
  Users, Clock, FileText, Shield, ArrowRight, X, Search, User,
  HardDrive, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ZeoChat from '@/components/ZeoChat';
import ZeoIcon from '@/components/ZeoIcon';
import UebernahmePruefPanel from '@/components/aktenuebernahme/UebernahmePruefPanel';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function AktenuebernahmePage() {
  const [files, setFiles] = useState([]);
  const [selectedKlient, setSelectedKlient] = useState(null);
  const [altweg, setAltweg] = useState({ gesperrt: false, text: null });   // AP6a: alter Analyseweg gesperrt, solange die Hintergrund-Uebernahme laeuft/offen ist
  const [klienten, setKlienten] = useState([]);
  const [klientSearch, setKlientSearch] = useState('');
  const [showKlientList, setShowKlientList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [chunksTotal, setChunksTotal] = useState(0);
  const [chunksDone, setChunksDone] = useState(0);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [events, setEvents] = useState([]);
  const [zeoText, setZeoText] = useState('');
  const [fortschritt, setFortschritt] = useState(0);
  const [error, setError] = useState(null);
  const [fertig, setFertig] = useState(false);
  const [ergebnisKlientId, setErgebnisKlientId] = useState(null);
  // Server-Dateien State
  const [serverDateien, setServerDateien] = useState([]);
  const [serverDateienLoading, setServerDateienLoading] = useState(false);
  const [serverKlientName, setServerKlientName] = useState('');
  const fileRef = useRef();
  const jobIdRef = useRef(null);
  const pollingRef = useRef(null);
  const navigate = useNavigate();

  const token = localStorage.getItem('olympus_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API}/api/betreuung/klienten`, { headers })
      .then(r => r.json())
      .then(data => setKlienten(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {                                     // AP6a: Sperrstatus des Altwegs (Server prueft unabhaengig davon selbst, 409)
    if (!selectedKlient) { setAltweg({ gesperrt: false, text: null }); return undefined; }
    let aus = false;
    const pruefe = () => fetch(`${API}/api/aktenuebernahme-pruefung/klient/${selectedKlient.id}/altweg`, { headers })
      .then((r) => (r.ok ? r.json() : { gesperrt: false, text: null }))      // 404 = Schalter aus -> Altweg unveraendert
      .then((j) => { if (!aus) setAltweg({ gesperrt: !!j.gesperrt, text: j.text || null }); })
      .catch(() => {});
    pruefe();
    const t = setInterval(pruefe, 15000);
    return () => { aus = true; clearInterval(t); };
  }, [selectedKlient]); // eslint-disable-line

  // Cleanup Polling beim Unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);


  // Server-Dateien laden wenn Klient ausgewählt
  const loadServerDateien = useCallback(async (klientId) => {
    setServerDateienLoading(true);
    try {
      const res = await fetch(`${API}/api/aktenuebernahme-v2/server-dateien/${klientId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setServerDateien(data.dateien || []);
        setServerKlientName(data.klient_name || '');
      } else {
        setServerDateien([]);
      }
    } catch {
      setServerDateien([]);
    } finally {
      setServerDateienLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (selectedKlient?.id) {
      loadServerDateien(selectedKlient.id);
    } else {
      setServerDateien([]);
      setServerKlientName('');
    }
  }, [selectedKlient, loadServerDateien]);

  // Server-Dateien analysieren
  async function startServerAnalyse() {
    if (!selectedKlient) return;
    setIsAnalysing(true);
    setEvents([]);
    setZeoText('');
    setError(null);
    setFertig(false);
    setFortschritt(0);
    toast.success(`${serverDateien.length} Server-Dateien werden analysiert...`);

    const MAX_RETRIES = 2;
    let retryCount = 0;

    const startStream = async () => {
      try {
        const response = await fetch(
          `${API}/api/aktenuebernahme-v2/server-analyse/${selectedKlient.id}`,
          { method: 'POST', headers }
        );

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          const detail = err.detail || `Server-Fehler ${response.status}`;
          throw new Error(detail);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              handleEvent(data);
            } catch { /* ignore parse errors */ }
          }
        }
      } catch (e) {
        // Auf Mobilgeraeten bricht die Verbindung manchmal ab ("Load failed")
        if (retryCount < MAX_RETRIES && (e.message?.includes('Load failed') || e.message?.includes('network') || e.message?.includes('aborted') || e.message?.includes('terminated'))) {
          retryCount++;
          toast.info(`Verbindung unterbrochen, Neuversuch ${retryCount}/${MAX_RETRIES}...`);
          await new Promise(r => setTimeout(r, 2000));
          return startStream();
        }
        setError(e.message);
        toast.error('Fehler: ' + e.message);
      } finally {
        setIsAnalysing(false);
      }
    };

    await startStream();
  }

  async function startRecrawl() {
    if (!selectedKlient) return;
    setIsAnalysing(true);
    setEvents([]);
    setZeoText('');
    setError(null);
    setFertig(false);
    setFortschritt(0);
    toast.success('Re-Crawl gestartet: Alle Dateien werden neu analysiert...');

    try {
      // Token pruefen
      try {
        const checkRes = await fetch(`${API}/api/auth/me`, { headers });
        if (!checkRes.ok) {
          setError('Sitzung abgelaufen — bitte neu einloggen.');
          toast.error('Token abgelaufen');
          setIsAnalysing(false);
          return;
        }
      } catch (e) { /* ignore */ }

      const response = await fetch(
        `${API}/api/aktenuebernahme-v2/recrawl/${selectedKlient.id}`,
        { method: 'POST', headers }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Server-Fehler ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            handleEvent(data);
          } catch { /* ignore */ }
        }
      }
    } catch (e) {
      setError(e.message);
      toast.error('Re-Crawl Fehler: ' + e.message);
    } finally {
      setIsAnalysing(false);
    }
  }


  function handleFiles(fileList) {
    const arr = Array.from(fileList);
    setFiles(arr);
    setEvents([]);
    setZeoText('');
    setError(null);
    setFertig(false);
    setFortschritt(0);
    toast.success(`${arr.length} Dateien ausgewaehlt`);
  }

  const filteredKlienten = klienten.filter(k => {
    if (!klientSearch) return true;
    const s = klientSearch.toLowerCase();
    return (k.vorname || '').toLowerCase().includes(s) ||
           (k.nachname || '').toLowerCase().includes(s);
  });

  async function uploadEinzeldatei(file, klientId, maxRetries = 2) {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Raw Binary Upload — umgeht Nginx HTTP/2 multipart/form-data Bug
        const res = await fetch(
          `${API}/api/aktenuebernahme-v2/einzelupload/${klientId}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/octet-stream',
              'X-Filename': encodeURIComponent(file.name)
            },
            body: file
          }
        );
        if (res.ok) return true;
        const errText = await res.text().catch(() => '');
        if (attempt < maxRetries && (res.status >= 500 || res.status === 0)) {
          console.warn(`Upload Retry ${attempt+1}: ${file.name} (Status ${res.status}) ${errText.slice(0,100)}`);
          await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
          continue;
        }
        console.error(`Upload fehlgeschlagen: ${file.name} (Status ${res.status}) ${errText.slice(0,200)}`);
        return false;
      } catch (e) {
        if (attempt < maxRetries) {
          console.warn(`Upload Netzwerk-Retry ${attempt+1}: ${file.name}`, e.message);
          await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
          continue;
        }
        console.error(`Upload Netzwerk-Fehler: ${file.name}`, e.message);
        return false;
      }
    }
    return false;
  }

  async function startAnalyse() {
    if (!files.length || !selectedKlient) {
      toast.error('Bitte Klient auswaehlen und Dateien hochladen');
      return;
    }
    setUploading(true);
    setUploadPhase('uploading');
    setUploadProgress(0);
    setChunksTotal(files.length);
    setChunksDone(0);
    setEvents([]);
    setZeoText('');
    setError(null);
    setFertig(false);

    try {
      // Token-Gültigkeit vor Upload prüfen
      try {
        const checkRes = await fetch(`${API}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!checkRes.ok) {
          setError('Sitzung abgelaufen — bitte neu einloggen (Logout → Login).');
          toast.error('Token abgelaufen — bitte neu einloggen');
          setUploading(false);
          return;
        }
      } catch (e) {
        console.warn('Token-Check fehlgeschlagen:', e.message);
      }

      const CONCURRENCY = 3;
      let fehler = 0;
      let done = 0;
      const fileList = [...files];

      // Parallelisierter Upload mit Concurrency-Limit
      const uploadWorker = async () => {
        while (fileList.length > 0) {
          const file = fileList.shift();
          if (!file) break;
          const ok = await uploadEinzeldatei(file, selectedKlient.id);
          if (!ok) fehler++;
          done++;
          setChunksDone(done);
          setUploadProgress(Math.round((done / files.length) * 100));
        }
      };

      const workers = Array.from({ length: Math.min(CONCURRENCY, files.length) }, () => uploadWorker());
      await Promise.all(workers);

      if (fehler > 0) {
        toast.warning(`${fehler} von ${files.length} Dateien konnten nicht hochgeladen werden`);
      }

      // Server-Dateien neu laden
      await loadServerDateien(selectedKlient.id);

      // Nur Analyse starten wenn mindestens 1 Datei erfolgreich hochgeladen wurde
      if (fehler >= files.length) {
        setError(`Alle ${files.length} Dateien konnten nicht hochgeladen werden. Mögliche Ursachen: Token abgelaufen (bitte neu einloggen), Netzwerkproblem oder Serverfehler. Prüfen Sie die Browser-Konsole (F12) für Details.`);
        toast.error('Upload komplett fehlgeschlagen — prüfen Sie die Browser-Konsole (F12)');
        return;
      }

      // Automatisch Server-Analyse starten
      setUploading(false);
      setUploadPhase('');
      toast.success(`${files.length - fehler} Dateien auf Server gespeichert – Analyse startet...`);
      await startServerAnalyse();

    } catch (e) {
      setError(e.message);
      toast.error('Fehler: ' + e.message);
    } finally {
      setUploading(false);
    }
  }

  function handleEvent(data) {
    switch (data.type) {
      case 'job_start':
        jobIdRef.current = data.job_id;
        // Polling starten als Fallback bei Stream-Abbruch
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = setInterval(async () => {
          if (!jobIdRef.current) return;
          try {
            const res = await fetch(`${API}/api/aktenuebernahme-v2/job/${jobIdRef.current}`, { headers });
            if (res.ok) {
              const job = await res.json();
              if (job.fortschritt > fortschritt) setFortschritt(job.fortschritt);
              if (job.status === 'fertig') {
                setFertig(true);
                setFortschritt(100);
                clearInterval(pollingRef.current);
              }
            }
          } catch { /* Polling-Fehler ignorieren */ }
        }, 5000);
        break;
      case 'zeo_token':
        setZeoText(prev => prev + data.token);
        break;
      case 'error':
        setError(data.message);
        break;
      case 'db_fertig':
        if (data.counts) {
          const parts = [];
          if (data.counts.stammdaten > 0) parts.push(`${data.counts.stammdaten} Stammdaten`);
          if (data.counts.personen > 0) parts.push(`${data.counts.personen} Kontakte`);
          if (data.counts.institutionen > 0) parts.push(`${data.counts.institutionen} Institutionen`);
          if (data.counts.dokumente > 0) parts.push(`${data.counts.dokumente} Dokumente`);
          if (data.counts.aufgaben > 0) parts.push(`${data.counts.aufgaben} Aufgaben`);
          if (data.counts.fristen > 0) parts.push(`${data.counts.fristen} Fristen`);
          if (data.counts.massnahmen > 0) parts.push(`${data.counts.massnahmen} Massnahmen (Finanzen/Versicherungen)`);
          if (data.counts.wohnsituation > 0) parts.push('Wohnsituation');
          if (parts.length > 0) {
            toast.success(`In Datenbank gespeichert: ${parts.join(', ')}`);
          }
        }
        if (data.fortschritt) setFortschritt(data.fortschritt);
        setEvents(prev => [...prev, data]);
        break;
      case 'fehlende_bescheide':
        if (data.bescheide && data.bescheide.length > 0) {
          toast.warning(`${data.anzahl} Bescheid(e) fehlen: ${data.bescheide.join(', ')}`, { duration: 10000 });
          setEvents(prev => [...prev, { ...data, type: 'fehlende_bescheide' }]);
        }
        break;
      case 'fertig':
        setFertig(true);
        setFortschritt(100);
        setErgebnisKlientId(data.klient_id);
        toast.success('Aktenuebernahme abgeschlossen!');
        if (pollingRef.current) clearInterval(pollingRef.current);
        jobIdRef.current = null;
        break;
      default:
        if (data.fortschritt) setFortschritt(data.fortschritt);
        if (data.type !== 'analyse_progress') {
          setEvents(prev => [...prev, data]);
        }
        break;
    }
  }

  const totalSize = files.reduce((s, f) => s + f.size, 0);
  const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(1);

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="aktenuebernahme-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shrink-0 shadow-md">
            <ZeoIcon size={40} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-100">KI-Aktenuebernahme</h1>
            <p className="text-sm text-slate-400 mt-1">
              Zeo analysiert alle Dokumente und erstellt eine vollstaendige Akte
            </p>
          </div>
        </div>
        {fertig && ergebnisKlientId && (
          <Button
            data-testid="zur-akte-btn"
            onClick={() => navigate(`/akten/${ergebnisKlientId}`)}
            className="bg-violet-600 hover:bg-violet-700"
          >
            Zur Akte <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Klient-Auswahl + Upload - nur wenn noch nicht analysiert */}
      {!isAnalysing && !fertig && (
        <>
          {/* Klient auswaehlen */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-slate-200">1. Klient auswaehlen</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedKlient ? (
                <div className="flex items-center gap-3 bg-slate-800/50 rounded-lg px-4 py-3" data-testid="selected-klient">
                  <User className="w-5 h-5 text-violet-400" />
                  <span className="text-slate-200 font-medium">
                    {selectedKlient.vorname} {selectedKlient.nachname}
                  </span>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => { setSelectedKlient(null); setShowKlientList(true); }}
                    className="ml-auto text-slate-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      data-testid="klient-search"
                      placeholder="Klient suchen..."
                      value={klientSearch}
                      onChange={e => { setKlientSearch(e.target.value); setShowKlientList(true); }}
                      onFocus={() => setShowKlientList(true)}
                      className="pl-10 bg-slate-800 border-slate-700 text-slate-200"
                    />
                  </div>
                  {showKlientList && (
                    <div className="absolute z-50 mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                      {filteredKlienten.slice(0, 20).map(k => (
                        <button
                          key={k.id}
                          className="w-full text-left px-4 py-2.5 hover:bg-slate-700 text-sm text-slate-200 border-b border-slate-700/50 last:border-0"
                          onClick={() => {
                            setSelectedKlient(k);
                            setShowKlientList(false);
                            setKlientSearch('');
                          }}
                        >
                          <span className="font-medium">{k.vorname} {k.nachname}</span>
                          {k.aktenzeichen && <span className="text-xs text-slate-500 ml-2">Az: {k.aktenzeichen}</span>}
                        </button>
                      ))}
                      {filteredKlienten.length === 0 && (
                        <div className="px-4 py-3 text-sm text-slate-500">Kein Klient gefunden</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* AP5: Hintergrund-Uebernahme + fachliche Pruefung (blendet sich ohne Server-Schalter aus) */}
          {selectedKlient && (
            <UebernahmePruefPanel klient={selectedKlient} klienten={klienten} api={API} headers={headers} />
          )}

          {/* Server-Dateien - erscheint wenn Klient ausgewählt */}
          {selectedKlient && (
            <Card className="bg-slate-900 border-slate-800" data-testid="server-dateien-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base text-slate-200 flex items-center gap-2">
                    <HardDrive className="w-4 h-4 text-emerald-400" />
                    2. Vorhandene Server-Dateien
                  </CardTitle>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => loadServerDateien(selectedKlient.id)}
                    className="text-slate-400 hover:text-white"
                    data-testid="refresh-server-dateien"
                  >
                    <RefreshCw className={`w-4 h-4 ${serverDateienLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {serverDateienLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-400 py-4 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Server-Ordner wird gescannt...
                  </div>
                ) : serverDateien.length > 0 ? (() => {
                  const unverarbeitet = serverDateien.filter(d => !d.einsortiert);
                  const einsortiert = serverDateien.filter(d => d.einsortiert);
                  return (
                  <div className="space-y-3">
                    <div className="bg-emerald-950/40 border border-emerald-800/50 rounded-lg px-4 py-3">
                      <div className="flex items-center gap-2 text-emerald-300 text-sm font-medium">
                        <FolderOpen className="w-4 h-4" />
                        {serverDateien.length} Dateien im Ordner "{serverKlientName}/"
                      </div>
                      <div className="text-xs text-emerald-400/70 mt-1">
                        {unverarbeitet.length} unverarbeitet | {einsortiert.length} bereits einsortiert
                        {' | '}{(serverDateien.reduce((s, d) => s + d.groesse, 0) / (1024 * 1024)).toFixed(1)} MB
                      </div>
                    </div>
                    {unverarbeitet.length > 0 && (
                      <>
                        <div className="text-xs text-slate-500 font-medium px-1">Unverarbeitet ({unverarbeitet.length}):</div>
                        <div className="max-h-36 overflow-y-auto space-y-1">
                          {unverarbeitet.slice(0, 100).map((d, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-slate-400 px-2 py-1.5 bg-slate-800/50 rounded">
                              <FileText className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                              <span className="truncate flex-1 text-slate-300">{d.name}</span>
                              <span className="text-slate-600 shrink-0 text-[10px]">{d.ordner}</span>
                              <span className="text-slate-500 shrink-0">{d.groesse_mb} MB</span>
                            </div>
                          ))}
                          {unverarbeitet.length > 100 && (
                            <div className="text-xs text-slate-500 text-center py-1">...und {unverarbeitet.length - 100} weitere</div>
                          )}
                        </div>
                        <div className="text-xs text-amber-400/70 bg-amber-950/30 border border-amber-800/30 rounded px-3 py-2">
                          {unverarbeitet.length > 50
                            ? `Es werden maximal 50 Dateien pro Durchlauf analysiert. Nach Abschluss erneut starten.`
                            : `Alle ${unverarbeitet.length} Dateien werden in einem Durchlauf analysiert.`}
                        </div>
                        {altweg.gesperrt && <div data-testid="altweg-gesperrt" className="text-xs text-amber-300 bg-amber-950/40 border border-amber-800/40 rounded px-3 py-2">{altweg.text} Die alte Analyse ist für diesen Klienten gesperrt, bis die Prüfung oben abgeschlossen ist.</div>}
                        <Button
                          data-testid="start-server-analyse-btn" disabled={altweg.gesperrt}
                          onClick={startServerAnalyse}
                          className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white h-12 text-base"
                        >
                          <Brain className="w-5 h-5 mr-2" />
                          {unverarbeitet.length > 50
                            ? `Erste 50 von ${unverarbeitet.length} Dateien analysieren`
                            : `${unverarbeitet.length} Dateien analysieren`}
                        </Button>
                      </>
                    )}
                    {einsortiert.length > 0 && unverarbeitet.length === 0 && (
                      <div className="space-y-3">
                        <div className="text-center py-2 text-sm text-emerald-400">
                          Alle Dateien sind bereits einsortiert.
                        </div>
                        {altweg.gesperrt && <div data-testid="altweg-gesperrt" className="text-xs text-amber-300 bg-amber-950/40 border border-amber-800/40 rounded px-3 py-2">{altweg.text} Die alte Analyse ist für diesen Klienten gesperrt, bis die Prüfung oben abgeschlossen ist.</div>}
                        <Button
                          data-testid="start-recrawl-btn" disabled={altweg.gesperrt}
                          onClick={startRecrawl}
                          variant="outline"
                          className="w-full border-amber-700 text-amber-400 hover:bg-amber-950/50 h-10"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Re-Crawl: Alle {einsortiert.length} Dateien neu sichten und umbenennen
                        </Button>
                        <div className="text-xs text-slate-500 px-1">
                          Liest alle Dateien erneut per OCR, benennt nach neuem Schema um und erstellt fehlende Aufgaben (Zuzahlungsbefreiung, Pflegegrad etc.)
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })() : (
                  <div className="text-center py-4 text-sm text-slate-500">
                    <HardDrive className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                    Kein Ordner oder keine Dateien auf dem Server gefunden
                    {serverKlientName && (
                      <div className="text-xs text-slate-600 mt-1">
                        Gesucht: {serverKlientName}/
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Datei-Upload */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-slate-200">
                {selectedKlient && serverDateien.length > 0 ? '3. Oder: Neue Dokumente hochladen' : (selectedKlient ? '2. Dokumente hochladen' : '2. Dokumente hochladen')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                data-testid="file-dropzone"
                className="border-2 border-dashed border-slate-700 rounded-xl p-8 text-center cursor-pointer hover:border-violet-500/50 hover:bg-slate-800/30 transition-all"
                onClick={() => fileRef.current?.click()}
                onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
                onDragOver={e => e.preventDefault()}
              >
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={e => handleFiles(e.target.files)}
                />
                {files.length > 0 ? (
                  <div className="space-y-2">
                    <Upload className="w-10 h-10 text-violet-400 mx-auto" />
                    <div className="text-lg font-semibold text-slate-200">{files.length} Dateien bereit</div>
                    <div className="text-sm text-slate-400">{totalSizeMB} MB gesamt</div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-10 h-10 text-slate-600 mx-auto" />
                    <div className="text-slate-400">Dateien hier ablegen oder klicken</div>
                    <div className="text-xs text-slate-600">PDF, Bilder, Word, Scans - Zeo analysiert alles</div>
                  </div>
                )}
              </div>

              {/* Upload-Fortschritt */}
              {uploading && (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">
                      Datei {chunksDone}/{chunksTotal} hochgeladen (5 parallel)
                    </span>
                    <span className="text-slate-400">{uploadProgress}%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  {chunksTotal > 100 && (
                    <div className="text-xs text-slate-500 text-center">
                      Grosse Uploads brauchen etwas Zeit - Browser-Tab offen lassen
                    </div>
                  )}
                </div>
              )}

              {/* Start-Button */}
              {files.length > 0 && selectedKlient && !uploading && (
                <>{altweg.gesperrt && <div data-testid="altweg-gesperrt-upload" className="text-xs text-amber-300 bg-amber-950/40 border border-amber-800/40 rounded px-3 py-2">{altweg.text} Die alte Analyse ist für diesen Klienten gesperrt, bis die Prüfung oben abgeschlossen ist.</div>}
                <Button
                  data-testid="start-analyse-btn" disabled={altweg.gesperrt}
                  onClick={startAnalyse}
                  className="w-full mt-4 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white h-12 text-base"
                >
                  <Brain className="w-5 h-5 mr-2" />
                  Hochgeladene Dateien analysieren - {files.length} Dokumente
                </Button></>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Zeo Chat */}
      {(isAnalysing || fertig || error) && (
        <ZeoChat
          events={events}
          zeoText={zeoText}
          isStreaming={isAnalysing}
          fortschritt={fortschritt}
          error={error}
        />
      )}

      {/* Fehlende Bescheide Warnung */}
      {fertig && events.some(e => e.type === 'fehlende_bescheide') && (
        <Card className="bg-amber-950/40 border-amber-700/50" data-testid="fehlende-bescheide-card">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium text-amber-200 text-sm">Bescheide fehlen</div>
                <div className="text-xs text-amber-300/80 mt-1">
                  Folgende Einnahmequellen wurden erkannt, aber kein zugehoeriger Bescheid gefunden:
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {events.find(e => e.type === 'fehlende_bescheide')?.bescheide?.map((b, i) => (
                    <span key={i} className="px-2 py-1 bg-amber-900/50 text-amber-200 text-xs rounded-md border border-amber-700/50">
                      {b}
                    </span>
                  ))}
                </div>
                <div className="text-xs text-amber-400/60 mt-2">
                  Aufgaben wurden automatisch erstellt. Bitte die fehlenden Bescheide einscannen oder den Betrag manuell nachtragen.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ergebnis-Aktionen */}
      {fertig && (
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="py-6">
            <div className="flex items-center gap-4 justify-center">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <div>
                <div className="text-lg font-semibold text-slate-200">Aktenuebernahme abgeschlossen</div>
                <div className="text-sm text-slate-400">Alle Dokumente wurden analysiert und einsortiert</div>
              </div>
              <div className="flex gap-3 ml-6">
                {ergebnisKlientId && (
                  <Button
                    onClick={() => navigate(`/akten/${ergebnisKlientId}`)}
                    className="bg-violet-600 hover:bg-violet-700"
                  >
                    Zur Akte
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    setFiles([]);
                    setEvents([]);
                    setZeoText('');
                    setError(null);
                    setFertig(false);
                    setFortschritt(0);
                    setSelectedKlient(null);
                  }}
                  className="border-slate-700 text-slate-300"
                >
                  Neue Uebernahme
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
